import { type HTMLAttributes, useCallback, useEffect, useMemo } from "react";

import { KubernetesMetric } from "@doitintl/cmp-models";
import {
  Autocomplete,
  type AutocompleteRenderGroupParams,
  type AutocompleteRenderInputParams,
  Box,
  type SxProps,
  TextField,
  type Theme,
  Tooltip,
  Typography,
} from "@mui/material";

import { metricFiltersText, reportText } from "../../../../../assets/texts";
import { useTrackKubernetesLensEventOnce } from "../../../../K8s/events";
import { createFilterOptions, getKubernetesBaseMetricsOptions } from "../../../utilities";
import type { MetricWSnap } from "../../../../../types";

type KubernetesMetricOption = {
  label: string;
  groupName: string;
  value: string;
  disabled?: boolean;
  tooltip?: string;
};

const emptyMetricOption: KubernetesMetricOption = {
  label: "",
  groupName: "",
  value: "",
};

export type KubernetesMetricSelectProps = {
  dataCy?: string;
  disabled?: boolean;
  disabledTooltip?: string;
  forcePopupIcon?: boolean;
  handleMetricSelect: (metricVal: KubernetesMetric, value: MetricWSnap | string) => void;
  metricsLoading?: boolean;
  selectedMetric: string | null | undefined;
  size?: "medium" | "small";
  sx?: SxProps<Theme>;
  inputLabel?: string;
};

const KubernetesMetricSelect = ({
  dataCy = "metric-select",
  disabled,
  disabledTooltip,
  forcePopupIcon = true,
  handleMetricSelect,
  inputLabel = metricFiltersText.METRIC_VALUE_LABEL,
  selectedMetric,
  size = "small",
  sx,
}: KubernetesMetricSelectProps) => {
  const metricOptionsFiltered = useMemo(() => getKubernetesBaseMetricsOptions, []);

  useTrackKubernetesLensEventOnce({
    name: "K8s Metric Event Viewed",
    properties: {
      metricName: selectedMetric,
    },
    condition: !!selectedMetric,
  });

  useEffect(() => {
    if (!selectedMetric || !Object.values(KubernetesMetric).includes(selectedMetric as KubernetesMetric)) {
      handleMetricSelect(KubernetesMetric.CPU_UTILIZATION, "");
    }
  }, [selectedMetric, handleMetricSelect]);

  const filterOptions = createFilterOptions<KubernetesMetricOption>({
    trim: true,
    ignoreAccents: true,
    ignoreCase: true,
    matchFrom: "any",
  });

  const handleChange = useCallback(
    (_, metricOption: KubernetesMetricOption | null) => {
      if (!metricOption) {
        return;
      }
      handleMetricSelect(metricOption?.value as KubernetesMetric, "");
    },
    [handleMetricSelect]
  );

  const metricOptions = useMemo(() => {
    const basicMetricsOptions: KubernetesMetricOption[] = metricOptionsFiltered.map((metric) => ({
      label: metric.label,
      groupName: reportText.EXTENDED_METRICS,
      value: metric.value.toString(),
      disabled: false,
    }));
    return [...basicMetricsOptions];
  }, [metricOptionsFiltered]);

  const selectedMetricOption = useMemo(() => {
    const selectedMetricValue = selectedMetric?.toString();

    if (!selectedMetricValue) {
      return emptyMetricOption;
    }
    return metricOptions.find((m) => m.value === selectedMetricValue) ?? emptyMetricOption;
  }, [metricOptions, selectedMetric]);

  const handleRenderInput = (params: AutocompleteRenderInputParams) => (
    <Tooltip title={disabled ? disabledTooltip : ""}>
      <TextField
        {...params}
        label={inputLabel}
        size={size}
        data-cy="metric-select-input"
        margin="dense"
        slotProps={{
          inputLabel: {
            shrink: true,
          },
        }}
      />
    </Tooltip>
  );

  const handleRenderOption = (props: HTMLAttributes<HTMLLIElement>, option: KubernetesMetricOption) => {
    const { key, ...restProps } = props as HTMLAttributes<HTMLLIElement> & { key?: React.Key };
    return (
      <Tooltip title={option.tooltip ?? ""} key={option.value}>
        <span>
          <li key={key} {...restProps} style={{ fontSize: "14px" }}>
            {option.label}
          </li>
        </span>
      </Tooltip>
    );
  };

  const handleRenderGroup = (params: AutocompleteRenderGroupParams) => (
    <Box key={params.key}>
      <Typography
        sx={{
          fontSize: "12px",
          color: "text.disabled",
          position: "sticky",
          top: "-8px",
          padding: "5px 10px",
          backgroundColor: "background.paper",
        }}
      >
        {params.group}
      </Typography>
      {params.children}
    </Box>
  );

  return (
    <Autocomplete
      data-cy={dataCy}
      disableClearable
      disabled={disabled}
      filterOptions={filterOptions}
      forcePopupIcon={forcePopupIcon}
      getOptionDisabled={(option) => option.disabled ?? false}
      groupBy={(option) => option.groupName}
      onChange={handleChange}
      options={metricOptions}
      renderGroup={handleRenderGroup}
      renderInput={handleRenderInput}
      renderOption={handleRenderOption}
      selectOnFocus={false}
      size={size}
      sx={sx}
      value={selectedMetricOption}
    />
  );
};

export default KubernetesMetricSelect;
